import { useState } from 'react';
import {
    Chip,
    Collapse,
    Grid,
    Typography,
    Box,
    IconButton,
    Button,
    Paper,
    CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import UpdateIcon from '@mui/icons-material/Update';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormTextField from '../../components/FormFields/FormTextField';
import { useAddNewAudienceMutation } from '../../api/audiencesApiSlice';
import UpdateButton from './adminUtils/updateButton';
import SearchBar from './adminUtils/SearchBar';
import PaginationInfo from './PaginationInfo';

function AudiencesList({ yleisöt, handleDeleteYleiso, handleUpdateYleiso, fetchData, onSuccess, onError }) {
    const [selectedYleiso, setSelectedYleiso] = useState(null);
    const [newYleisoData, setNewYleisoData] = useState({ yleiso: '' });
    const [addYleisoData, setAddYleisoData] = useState({ yleiso: '' });
    const [showAddForm, setShowAddForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const itemsPerPage = 10;
    const [addNewYleiso, { isLoading }] = useAddNewAudienceMutation();

    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        setCurrentPage(1);
    };

    const filteredYleisöt = yleisöt.filter((Yleiso) =>
        Yleiso.yleiso.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const totalPages = Math.ceil(filteredYleisöt.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedYleisöt = filteredYleisöt.slice(startIndex, endIndex);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleUserClick = (Yleiso) => {
        setShowAddForm(false);
        if (selectedYleiso?.id === Yleiso.id) {
            setSelectedYleiso(null);
            setNewYleisoData({ yleiso: '' });
        } else {
            setSelectedYleiso(Yleiso);
            setNewYleisoData(Yleiso);
        }
    };

    const handleAddClick = () => {
        setSelectedYleiso(null);
        setNewYleisoData({ yleiso: '' });
        setShowAddForm(!showAddForm);
    };

    const handleLisääYleiso = async (Yleiso) => {
        try {
            const result = await addNewYleiso(Yleiso);
            if (result.error) onError('Yleisön luominen epäonnistui');
            if (!result.error) onSuccess('Yleisön luominen onnistui.', 'yleisot', result.data.id);
            setAddYleisoData({ yleiso: '' });
            setShowAddForm(false);
            fetchData();
        } catch (error) {
            console.error(error);
            onError('Yleisön luominen epäonnistui');
        }
    };

    return (
        <Box sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                {/* Header and Search */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Kohdeyleisöt
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} />
                    </Grid>
                </Grid>

                {/* Audience List */}
                {displayedYleisöt.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                        Ei kohdeyleisöjä näytettäväksi.
                    </Typography>
                ) : (
                    displayedYleisöt.map((Yleiso) => {
                        const isSelected = selectedYleiso?.id === Yleiso.id;

                        return (
                            <Box key={Yleiso?.id} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'taphaBlue.main' : 'grey.200',
                                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.06)' : 'taphaWhite.main',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        '&:hover': {
                                            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'grey.50',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                            cursor: 'pointer',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                    onClick={() => handleUserClick(Yleiso)}
                                >
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {Yleiso?.yleiso}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            Kohdeyleisö tapahtumien luokitteluun
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                        </Box>
                                    </Box>
                                    <IconButton
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleDeleteYleiso(event, Yleiso);
                                            if (selectedYleiso?.id === Yleiso.id) {
                                                setSelectedYleiso(null);
                                                setNewYleisoData({ yleiso: '' });
                                            }
                                        }}
                                        sx={{ color: 'error.main', flexShrink: 0 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>

                                <Collapse in={isSelected} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                            Muokkaa yleisöä
                                        </Typography>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={8}>
                                                <FormTextField
                                                    fieldName="yleiso"
                                                    label="Yleisö"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newYleisoData.yleiso}
                                                    onChange={(event) => {
                                                        const { name, value } = event.target;
                                                        setNewYleisoData((prevState) => ({ ...prevState, [name]: value }));
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <UpdateButton handleUpdate={handleUpdateYleiso} data={newYleisoData} />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })
                )}

                {/* Add Form */}
                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={showAddForm ? <CloseIcon /> : <AddIcon />}
                        onClick={handleAddClick}
                        sx={{
                            bgcolor: showAddForm ? 'grey.600' : 'taphaBlue.main',
                            '&:hover': { bgcolor: showAddForm ? 'grey.700' : 'taphaBlue.dark' },
                        }}
                    >
                        {showAddForm ? 'Sulje' : 'Lisää yleisö'}
                    </Button>
                    {showAddForm && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                Lisää uusi yleisö
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={8}>
                                    <FormTextField
                                        key="addYleiso"
                                        fieldName="yleiso"
                                        label="Yleisö"
                                        multiline={true}
                                        fullWidth={true}
                                        value={addYleisoData.yleiso}
                                        onChange={(event) =>
                                            setAddYleisoData({ ...addYleisoData, yleiso: event.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={isLoading ? null : <AddIcon />}
                                        onClick={() => handleLisääYleiso(addYleisoData)}
                                        disabled={isLoading}
                                        sx={{ bgcolor: 'taphaGreen.main', '&:hover': { bgcolor: 'taphaGreen.dark' } }}
                                    >
                                        {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Lisää'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Box>

                {/* Pagination */}
                {filteredYleisöt.length > itemsPerPage && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PaginationInfo
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            filteredArray={filteredYleisöt}
                            array={yleisöt}
                        />
                        <Box sx={{ mt: 1 }}>
                            <IconButton
                                size="small"
                                onClick={handlePreviousPage}
                                disabled={currentPage <= 1}
                                sx={{ mr: 1 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleNextPage}
                                disabled={currentPage >= totalPages}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default AudiencesList;